import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { useAdminGetSessionAdmissionCardsQuery } from "../../api/examsApi";
import ExamAdmissionCard from "../../components/ExamAdmissionCard";

export default function AdminSessionCardsPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { data, isLoading, isError } = useAdminGetSessionAdmissionCardsQuery(Number(sessionId));
  const cards: any[] = data?.cards ?? [];

  const examStages: string[] = Array.from(
    new Set(cards.map((c) => c.applicant?.exam_stage).filter(Boolean))
  ).sort() as string[];

  const centerNames: string[] = Array.from(
    new Set(cards.map((c) => c.applicant?.center).filter(Boolean))
  ).sort() as string[];

  const [selectedExam, setSelectedExam] = useState("__all__");
  const [selectedCenter, setSelectedCenter] = useState("__all__");
  const [selectedCourse, setSelectedCourse] = useState("__all__");

  const examCourses: { id: string | number; code: string; name: string }[] = (() => {
    if (selectedExam === "__all__") return [];
    const seen = new Map<string, { id: string | number; code: string; name: string }>();
    for (const card of cards) {
      if (card.applicant?.exam_stage !== selectedExam) continue;
      if (selectedCenter !== "__all__" && card.applicant?.center !== selectedCenter) continue;
      for (const course of card.applicant?.courses ?? []) {
        if (course?.id && !seen.has(String(course.id))) {
          seen.set(String(course.id), course);
        }
      }
    }
    return Array.from(seen.values()).sort((a, b) => a.code.localeCompare(b.code));
  })();

  const filtered = cards.filter((c) => {
    const examMatch = selectedExam === "__all__" || c.applicant?.exam_stage === selectedExam;
    const centerMatch = selectedCenter === "__all__" || c.applicant?.center === selectedCenter;
    const courseMatch =
      selectedCourse === "__all__" ||
      (c.applicant?.courses ?? []).some((cr: any) => String(cr?.id) === selectedCourse);
    return examMatch && centerMatch && courseMatch;
  });

  return (
    <>
      <style>{`@media print { .no-print { display: none !important; } .print-card { page-break-after: always; } }`}</style>
      <div className="p-6 space-y-6 bg-[#F8FAFC] min-h-screen">
        <div className="no-print flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Link to={`/exams/admin/sessions/${sessionId}`} className="p-2 rounded-xl hover:bg-slate-200 transition-all">
              <ArrowLeft className="w-4 h-4 text-slate-600" />
            </Link>
            <div>
              <h1 className="text-lg font-black text-slate-900 uppercase tracking-widest">Admission Cards</h1>
              <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">
                {filtered.length} of {cards.length} card{cards.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <select
              value={selectedExam}
              onChange={(e) => { setSelectedExam(e.target.value); setSelectedCourse("__all__"); }}
              className="border border-slate-200 rounded-xl px-3 py-2 text-xs bg-slate-50 outline-none"
            >
              <option value="__all__">All Exams</option>
              {examStages.map((e) => (
                <option key={e} value={e}>{e}</option>
              ))}
            </select>

            <select
              value={selectedCenter}
              onChange={(e) => { setSelectedCenter(e.target.value); setSelectedCourse("__all__"); }}
              className="border border-slate-200 rounded-xl px-3 py-2 text-xs bg-slate-50 outline-none"
            >
              <option value="__all__">All Centers</option>
              {centerNames.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>

            <select
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
              disabled={selectedExam === "__all__" || examCourses.length === 0}
              className="border border-slate-200 rounded-xl px-3 py-2 text-xs bg-slate-50 outline-none disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <option value="__all__">
                {selectedExam === "__all__"
                  ? "Select an exam first"
                  : examCourses.length === 0
                  ? "No courses registered"
                  : "All Courses"}
              </option>
              {examCourses.map((c) => (
                <option key={c.id} value={String(c.id)}>{c.code} — {c.name}</option>
              ))}
            </select>

            <button
              onClick={() => window.print()}
              disabled={isLoading || filtered.length === 0}
              className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-orange-600 disabled:opacity-50 transition-all"
            >
              <Printer className="w-4 h-4" /> Print ({filtered.length})
            </button>
          </div>
        </div>

        {isLoading && <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>}
        {isError && <div className="bg-rose-50 border border-rose-200 rounded-2xl p-8 text-center"><p className="text-rose-700 font-black text-sm">Could not load cards.</p></div>}
        {!isLoading && filtered.length === 0 && cards.length > 0 && (
          <div className="bg-white rounded-[2rem] border border-slate-200 p-16 text-center">
            <p className="text-slate-400 font-black uppercase tracking-widest text-sm">No cards match the selected filters.</p>
          </div>
        )}
        {!isLoading && cards.length === 0 && (
          <div className="bg-white rounded-[2rem] border border-slate-200 p-16 text-center">
            <p className="text-slate-400 font-black uppercase tracking-widest text-sm">No submitted applications for this session yet.</p>
          </div>
        )}

        <div className="space-y-8">
          {filtered.map((card, idx) => (
            <div key={idx} className="print-card flex justify-center">
              <div className="shadow-lg"><ExamAdmissionCard data={card} /></div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
